/**
 * webAsyncStorage.js
 *
 * Drop-in replacement for `@react-native-async-storage/async-storage` in the
 * **web / Tauri-webview build only** (wired through `config.resolve.alias` in
 * `webpack.config.js`). Native builds keep the real package untouched.
 *
 * ### Why
 * The upstream web implementation is a thin wrapper over `window.localStorage`,
 * which every browser caps at ~5 MB for the WHOLE origin — shared with
 * redux-persist. The POS caches a store document, a 1000+ item food catalog,
 * categories, orders, bookings, VIP cards and up to five mail bodies in there,
 * so the cap is reached in normal use and the next write throws
 * `QuotaExceededError: Failed to execute 'setItem' on 'Storage'`.
 *
 * localStorage is also the wrong engine for this workload on a low-end device:
 * it is **synchronous** and stores UTF-16, so persisting a 3 MB catalog blocks
 * the main thread for the whole serialize-and-copy and costs 6 MB of the budget.
 *
 * IndexedDB — a real transactional store, the browser's equivalent of putting
 * the data in a database instead of a string bag — removes both problems:
 * quota is per-origin storage pressure (hundreds of MB, and eligible for
 * `navigator.storage.persist()`), and every operation is asynchronous and
 * off-thread. The AsyncStorage API is already promise-based, so no call site
 * changes.
 *
 * ### Behaviour
 * - **Migration.** The first open copies existing localStorage entries into
 *   IndexedDB and then clears them, which both preserves the signed-in session
 *   across the upgrade and immediately returns the 5 MB to the browser.
 *   redux-persist's own `persist:*` keys are left alone — that library talks to
 *   localStorage directly, not through this module.
 * - **Hot mirror.** Values at or below `MIRROR_MAX_CHARS` are kept in an
 *   in-memory `Map`, so the hot keys (`restaurant`, tokens, settings) resolve
 *   without an IndexedDB round trip. Large values (catalogs, order lists) are
 *   deliberately NOT mirrored — holding a multi-MB string in RAM is exactly the
 *   pressure we are trying to remove on a low-RAM device.
 * - **Fallback.** If IndexedDB is missing or refuses to open (private browsing,
 *   blocked storage, an old WebView) every call degrades to the same
 *   localStorage behaviour as before, so this module can never be worse than
 *   what it replaces. `helpers/storageQuota` still guards those writes.
 *
 * @module helpers/webAsyncStorage
 */

/* global indexedDB, setTimeout, clearTimeout */

/** IndexedDB database name. @type {string} */
const DB_NAME = 'happy-app-storage';
/** Object store holding the key → serialized-value pairs. @type {string} */
const STORE_NAME = 'kv';
/** Schema version. Bump only alongside an `onupgradeneeded` migration. @type {number} */
const DB_VERSION = 1;
/** Marker key recording that the one-time localStorage import already ran. @type {string} */
const MIGRATION_FLAG = '__happy_idb_migrated__';
/**
 * Values longer than this are not kept in the in-memory mirror. 64K chars
 * (~128 KB UTF-16) covers the store document, session and every settings key
 * while leaving catalogs and order lists on disk.
 * @type {number}
 */
const MIRROR_MAX_CHARS = 64 * 1024;
/** localStorage key prefixes this module must not touch. @type {string[]} */
const FOREIGN_PREFIXES = ['persist:'];

/** In-memory mirror of small values. @type {Map<string, string>} */
const mirror = new Map();
/** Keys known to exist in IndexedDB but too large to mirror. @type {Set<string>} */
const largeKeys = new Set();

/** @type {IDBDatabase|null} */
let db = null;
/** @type {Promise<IDBDatabase|null>|null} */
let readyPromise = null;
/** True once we have given up on IndexedDB and fall back to localStorage. @type {boolean} */
let useFallback = false;

/**
 * Whether a localStorage key belongs to another library and must be left alone.
 *
 * @param {string} key Storage key.
 * @returns {boolean} true when the key is not ours to migrate or clear.
 */
const isForeignKey = (key) => FOREIGN_PREFIXES.some((prefix) => key.startsWith(prefix));

/**
 * The browser's localStorage, or null when it is unavailable (SSR, disabled
 * cookies, sandboxed iframe). Accessing `window.localStorage` can itself throw
 * in some privacy modes, hence the try/catch.
 *
 * @returns {Storage|null} The Storage object, or null.
 */
const getLocalStorage = () => {
    try {
        return (typeof window !== 'undefined' && window.localStorage) ? window.localStorage : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Wrap an IDBRequest in a promise.
 *
 * @param {IDBRequest} request The request to await.
 * @returns {Promise<*>} Resolves with `request.result`, rejects with `request.error`.
 */
const promisifyRequest = (request) => new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('IndexedDB request failed'));
});

/**
 * Run a transaction against the key-value store and resolve when it COMMITS
 * (not merely when the last request succeeds) — a quota failure surfaces on the
 * transaction's `onabort`, so resolving earlier would report a write as
 * successful that the browser then rolled back.
 *
 * @param {IDBTransactionMode} mode `'readonly'` or `'readwrite'`.
 * @param {(store: IDBObjectStore) => *} work Callback issuing the requests.
 * @returns {Promise<*>} The callback's resolved value once the transaction commits.
 */
const withStore = async (mode, work) => {
    const database = await open();

    if (!database) {
        throw new Error('IndexedDB unavailable');
    }

    return new Promise((resolve, reject) => {
        let result;
        let transaction;

        try {
            transaction = database.transaction(STORE_NAME, mode);
        } catch (error) {
            reject(error);

            return;
        }

        transaction.oncomplete = () => resolve(result);
        transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted'));
        transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed'));

        try {
            Promise.resolve(work(transaction.objectStore(STORE_NAME)))
                .then((value) => {
                    result = value;
                })
                .catch(reject);
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * One-time import of everything the old localStorage-backed AsyncStorage wrote.
 *
 * Copies first, clears second, and only clears the keys it actually copied — so
 * an interrupted migration never loses the session, and another library's keys
 * are never touched.
 *
 * @param {IDBDatabase} database The freshly opened database.
 * @returns {Promise<void>} Resolves when the import finished or was skipped.
 */
const migrateFromLocalStorage = async (database) => {
    const store = getLocalStorage();

    if (!store) {
        return;
    }

    try {
        const alreadyMigrated = await new Promise((resolve, reject) => {
            const transaction = database.transaction(STORE_NAME, 'readonly');
            const request = transaction.objectStore(STORE_NAME).get(MIGRATION_FLAG);
            request.onsuccess = () => resolve(typeof request.result === 'string');
            request.onerror = () => reject(request.error);
        });

        if (alreadyMigrated) {
            return;
        }

        const entries = [];

        for (let index = 0; index < store.length; index += 1) {
            const key = store.key(index);

            if (key && !isForeignKey(key) && key !== MIGRATION_FLAG) {
                const value = store.getItem(key);

                if (typeof value === 'string') {
                    entries.push([key, value]);
                }
            }
        }

        await new Promise((resolve, reject) => {
            const transaction = database.transaction(STORE_NAME, 'readwrite');
            const objectStore = transaction.objectStore(STORE_NAME);
            entries.forEach(([key, value]) => objectStore.put(value, key));
            objectStore.put(new Date().toISOString(), MIGRATION_FLAG);
            transaction.oncomplete = resolve;
            transaction.onabort = () => reject(transaction.error);
            transaction.onerror = () => reject(transaction.error);
        });

        // Only now is it safe to hand the 5 MB back to the browser.
        entries.forEach(([key, value]) => {
            try {
                store.removeItem(key);
            } catch (_error) {
                // A key we cannot remove is harmless — it is already copied.
            }
            remember(key, value);
        });
    } catch (error) {
        console.warn('[webAsyncStorage] localStorage import skipped:', error?.message || error);
    }
};

/**
 * Open (and cache) the database. Resolves to null when IndexedDB is unusable,
 * which switches every operation to the localStorage fallback for the rest of
 * the session.
 *
 * @returns {Promise<IDBDatabase|null>} The open database, or null.
 */
const open = () => {
    if (readyPromise) {
        return readyPromise;
    }

    readyPromise = new Promise((resolve) => {
        if (typeof indexedDB === 'undefined' || !indexedDB) {
            useFallback = true;
            resolve(null);

            return;
        }

        let request;

        try {
            request = indexedDB.open(DB_NAME, DB_VERSION);
        } catch (error) {
            console.warn('[webAsyncStorage] IndexedDB open threw, falling back to localStorage:', error?.message || error);
            useFallback = true;
            resolve(null);

            return;
        }

        // Safari can leave an open() request pending forever when storage is
        // blocked; never let boot hang on it.
        const timeout = setTimeout(() => {
            if (!db) {
                console.warn('[webAsyncStorage] IndexedDB open timed out, falling back to localStorage');
                useFallback = true;
                resolve(null);
            }
        }, 4000);

        /**
         * Whether a late `onsuccess` must be ignored because the timeout above
         * already switched the session to localStorage. Adopting the database at
         * that point would run the migration — which DELETES the localStorage
         * copies — while every read still goes to localStorage, losing the data.
         *
         * @returns {boolean} true when the open resolved too late to be used.
         */
        const isTooLate = () => useFallback;

        request.onupgradeneeded = () => {
            const database = request.result;

            if (!database.objectStoreNames.contains(STORE_NAME)) {
                database.createObjectStore(STORE_NAME);
            }
        };

        request.onerror = () => {
            clearTimeout(timeout);
            console.warn('[webAsyncStorage] IndexedDB unavailable, falling back to localStorage:', request.error?.message || request.error);
            useFallback = true;
            resolve(null);
        };

        request.onblocked = () => {
            clearTimeout(timeout);
            useFallback = true;
            resolve(null);
        };

        request.onsuccess = async () => {
            clearTimeout(timeout);

            if (isTooLate()) {
                try {
                    request.result.close();
                } catch (_error) {
                    // Nothing to do — the handle is being abandoned anyway.
                }

                return;
            }

            db = request.result;
            // A second tab upgrading the schema closes this handle; drop the
            // cached open so the next call re-opens instead of using a dead DB.
            db.onclose = () => {
                db = null;
                readyPromise = null;
            };
            await migrateFromLocalStorage(db);
            resolve(db);
        };
    });

    return readyPromise;
};

/**
 * Record a value in the in-memory mirror, honouring the size cut-off.
 *
 * @param {string} key Storage key.
 * @param {string|null} value Value just written, or null when removed.
 * @returns {void}
 */
const remember = (key, value) => {
    if (value === null || typeof value === 'undefined') {
        mirror.delete(key);
        largeKeys.delete(key);

        return;
    }

    if (value.length <= MIRROR_MAX_CHARS) {
        mirror.set(key, value);
        largeKeys.delete(key);
    } else {
        mirror.delete(key);
        largeKeys.add(key);
    }
};

/* ─────────────────────────── localStorage fallback ───────────────────────── */

/**
 * @param {string} key Storage key.
 * @returns {string|null} The stored value, or null.
 */
const fallbackGet = (key) => {
    const store = getLocalStorage();

    return store ? store.getItem(key) : (mirror.has(key) ? mirror.get(key) : null);
};

/**
 * @param {string} key Storage key.
 * @param {string} value Value to store.
 * @returns {void}
 * @throws {DOMException} `QuotaExceededError` when localStorage is full — the
 *   caller (`helpers/storageQuota`) is what turns that into a reclaim + retry.
 */
const fallbackSet = (key, value) => {
    const store = getLocalStorage();

    if (!store) {
        mirror.set(key, value);

        return;
    }

    store.setItem(key, value);
};

/**
 * @param {string} key Storage key.
 * @returns {void}
 */
const fallbackRemove = (key) => {
    const store = getLocalStorage();

    if (store) {
        store.removeItem(key);
    }
    mirror.delete(key);
};

/**
 * @returns {string[]} Every key this module owns.
 */
const fallbackKeys = () => {
    const store = getLocalStorage();

    if (!store) {
        return Array.from(mirror.keys());
    }

    const keys = [];

    for (let index = 0; index < store.length; index += 1) {
        const key = store.key(index);

        if (key && !isForeignKey(key) && key !== MIGRATION_FLAG) {
            keys.push(key);
        }
    }

    return keys;
};

/* ───────────────────────────────── API ───────────────────────────────────── */

/**
 * Invoke an AsyncStorage-style optional callback without letting a throwing
 * callback reject the promise the caller is awaiting.
 *
 * Arity matters: upstream calls a write callback as `callback(error)` and a read
 * callback as `callback(error, value)`, so the value argument is forwarded only
 * when the caller actually supplied one.
 *
 * @param {Function|undefined} callback Optional `(error[, value])` callback.
 * @param {Error|null} error Error to report, or null.
 * @param {...*} value Result to report, for read-shaped callbacks only.
 * @returns {void}
 */
const invoke = (callback, error, ...value) => {
    if (typeof callback === 'function') {
        try {
            callback(error, ...value);
        } catch (_error) {
            // A broken consumer callback must not corrupt the storage promise.
        }
    }
};

/**
 * Read one value.
 *
 * @param {string} key Storage key.
 * @param {Function} [callback] Optional `(error, value)` callback.
 * @returns {Promise<string|null>} The stored value, or null when absent.
 */
const getItem = async (key, callback) => {
    try {
        if (mirror.has(key)) {
            const value = mirror.get(key);
            invoke(callback, null, value);

            return value;
        }

        if (useFallback || !(await open())) {
            const value = fallbackGet(key);
            invoke(callback, null, value);

            return value;
        }

        const raw = await withStore('readonly', (store) => promisifyRequest(store.get(key)));
        const value = typeof raw === 'string' ? raw : null;
        remember(key, value);
        invoke(callback, null, value);

        return value;
    } catch (error) {
        invoke(callback, error);

        return null;
    }
};

/**
 * Write one value.
 *
 * @param {string} key Storage key.
 * @param {string} value Value to store.
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once the transaction committed.
 * @throws {Error} Propagates storage failures (including `QuotaExceededError`)
 *   so `helpers/storageQuota` can reclaim and retry.
 */
const setItem = async (key, value, callback) => {
    const serialized = typeof value === 'string' ? value : String(value);

    try {
        if (useFallback || !(await open())) {
            fallbackSet(key, serialized);
        } else {
            await withStore('readwrite', (store) => promisifyRequest(store.put(serialized, key)));
        }

        remember(key, serialized);
        invoke(callback, null);
    } catch (error) {
        // Keep the mirror honest: a failed write must not leave a value visible
        // to `getItem` that the store never accepted.
        mirror.delete(key);
        invoke(callback, error);

        throw error;
    }
};

/**
 * Delete one value.
 *
 * @param {string} key Storage key.
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once the key is gone.
 */
const removeItem = async (key, callback) => {
    try {
        if (useFallback || !(await open())) {
            fallbackRemove(key);
        } else {
            await withStore('readwrite', (store) => promisifyRequest(store.delete(key)));
        }

        remember(key, null);
        invoke(callback, null);
    } catch (error) {
        invoke(callback, error);

        throw error;
    }
};

/**
 * List every key.
 *
 * @param {Function} [callback] Optional `(error, keys)` callback.
 * @returns {Promise<string[]>} All keys, excluding this module's own marker.
 */
const getAllKeys = async (callback) => {
    try {
        if (useFallback || !(await open())) {
            const keys = fallbackKeys();
            invoke(callback, null, keys);

            return keys;
        }

        const raw = await withStore('readonly', (store) => promisifyRequest(store.getAllKeys()));
        const keys = (Array.isArray(raw) ? raw : [])
            .map((key) => String(key))
            .filter((key) => key !== MIGRATION_FLAG);
        invoke(callback, null, keys);

        return keys;
    } catch (error) {
        invoke(callback, error, []);

        return [];
    }
};

/**
 * Remove every key this module owns.
 *
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once the store is empty.
 */
const clear = async (callback) => {
    try {
        if (useFallback || !(await open())) {
            fallbackKeys().forEach(fallbackRemove);
        } else {
            // Keep the migration marker so signing out never re-imports the
            // localStorage copies we already deleted.
            await withStore('readwrite', async (store) => {
                const keys = await promisifyRequest(store.getAllKeys());
                (Array.isArray(keys) ? keys : [])
                    .filter((key) => String(key) !== MIGRATION_FLAG)
                    .forEach((key) => store.delete(key));
            });
        }

        mirror.clear();
        largeKeys.clear();
        invoke(callback, null);
    } catch (error) {
        invoke(callback, error);

        throw error;
    }
};

/**
 * Read many values in one transaction.
 *
 * @param {string[]} keys Keys to read.
 * @param {Function} [callback] Optional `(error, entries)` callback.
 * @returns {Promise<Array<[string, string|null]>>} `[key, value]` pairs, in order.
 */
const multiGet = async (keys = [], callback) => {
    const list = Array.isArray(keys) ? keys.map((key) => String(key)) : [];

    try {
        if (useFallback || !(await open())) {
            const entries = list.map((key) => [key, fallbackGet(key)]);
            invoke(callback, null, entries);

            return entries;
        }

        const values = await withStore('readonly', (store) => Promise.all(
            list.map((key) => promisifyRequest(store.get(key))),
        ));
        const entries = list.map((key, index) => {
            const value = typeof values[index] === 'string' ? values[index] : null;
            remember(key, value);

            return [key, value];
        });
        invoke(callback, null, entries);

        return entries;
    } catch (error) {
        invoke(callback, error, []);

        return list.map((key) => [key, null]);
    }
};

/**
 * Write many values in one transaction — atomic, so a quota failure rolls the
 * whole batch back rather than leaving it half-applied.
 *
 * @param {Array<[string, string]>} pairs `[key, value]` pairs to store.
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once the transaction committed.
 */
const multiSet = async (pairs = [], callback) => {
    const list = (Array.isArray(pairs) ? pairs : [])
        .filter((pair) => Array.isArray(pair) && pair.length >= 1)
        .map(([key, value]) => [String(key), typeof value === 'string' ? value : String(value)]);

    try {
        if (useFallback || !(await open())) {
            list.forEach(([key, value]) => fallbackSet(key, value));
        } else {
            await withStore('readwrite', (store) => Promise.all(
                list.map(([key, value]) => promisifyRequest(store.put(value, key))),
            ));
        }

        list.forEach(([key, value]) => remember(key, value));
        invoke(callback, null);
    } catch (error) {
        list.forEach(([key]) => mirror.delete(key));
        invoke(callback, error);

        throw error;
    }
};

/**
 * Delete many values in one transaction.
 *
 * @param {string[]} keys Keys to delete.
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once the keys are gone.
 */
const multiRemove = async (keys = [], callback) => {
    const list = Array.isArray(keys) ? keys.map((key) => String(key)) : [];

    try {
        if (useFallback || !(await open())) {
            list.forEach(fallbackRemove);
        } else {
            await withStore('readwrite', (store) => Promise.all(
                list.map((key) => promisifyRequest(store.delete(key))),
            ));
        }

        list.forEach((key) => remember(key, null));
        invoke(callback, null);
    } catch (error) {
        invoke(callback, error);

        throw error;
    }
};

/**
 * Shallow-merge a JSON object into the value already stored under `key`.
 * Matches the upstream contract: both sides must serialize to JSON objects.
 *
 * @param {string} key Storage key.
 * @param {string} value JSON object to merge in.
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once the merged value is stored.
 */
const mergeItem = async (key, value, callback) => {
    try {
        const existing = await getItem(key);
        const merged = existing
            ? JSON.stringify({ ...JSON.parse(existing), ...JSON.parse(value) })
            : value;
        await setItem(key, merged);
        invoke(callback, null);
    } catch (error) {
        invoke(callback, error);

        throw error;
    }
};

/**
 * Merge many JSON objects.
 *
 * @param {Array<[string, string]>} pairs `[key, jsonObject]` pairs.
 * @param {Function} [callback] Optional `(error)` callback.
 * @returns {Promise<void>} Resolves once every merge is stored.
 */
const multiMerge = async (pairs = [], callback) => {
    try {
        for (const [key, value] of (Array.isArray(pairs) ? pairs : [])) {
            await mergeItem(String(key), value);
        }
        invoke(callback, null);
    } catch (error) {
        invoke(callback, error);

        throw error;
    }
};

/**
 * No-op kept for API parity — the upstream web build batches `getItem` calls and
 * exposes this to flush them; every read here is already independent.
 *
 * @returns {void}
 */
const flushGetRequests = () => {};

const AsyncStorage = {
    getItem,
    setItem,
    removeItem,
    mergeItem,
    clear,
    getAllKeys,
    flushGetRequests,
    multiGet,
    multiSet,
    multiRemove,
    multiMerge,
};

/**
 * Hook-shaped accessor mirroring the upstream `useAsyncStorage` export.
 *
 * @param {string} key Storage key to bind to.
 * @returns {{getItem: Function, setItem: Function, mergeItem: Function, removeItem: Function}}
 *   Key-bound storage operations.
 */
export const useAsyncStorage = (key) => ({
    getItem: (callback) => getItem(key, callback),
    setItem: (value, callback) => setItem(key, value, callback),
    mergeItem: (value, callback) => mergeItem(key, value, callback),
    removeItem: (callback) => removeItem(key, callback),
});

export default AsyncStorage;
